/**
 * Trunnion AI - Command Palette Context Provider
 * Manages the command registry, open/close state, and Cmd+K hotkey binding.
 * Wrap your app (inside Router) with this provider to enable the command palette.
 */

import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  GitBranch,
  Play,
  Bot,
  Shield,
  Settings,
  Plus,
  UserPlus,
} from 'lucide-react';
import { useHotkey } from '@/hooks/useHotkeys';
import { SHORTCUTS } from '@/config/shortcuts';
import { ROUTES } from '@/config/routes';
import { formatShortcut } from '@/hooks/useHotkeys';
import type { Command } from '@/components/command-palette/CommandItem';
import { CommandPalette } from '@/components/command-palette/CommandPalette';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface CommandPaletteContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  commands: Command[];
  registerCommand: (cmd: Command) => void;
  unregisterCommand: (id: string) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const CommandPaletteContext = createContext<CommandPaletteContextValue>({
  isOpen: false,
  open: () => {},
  close: () => {},
  commands: [],
  registerCommand: () => {},
  unregisterCommand: () => {},
});

// ---------------------------------------------------------------------------
// Helper: resolve shortcut display string from SHORTCUTS config by id
// ---------------------------------------------------------------------------

function shortcutDisplayFor(shortcutId: string): string | undefined {
  const def = SHORTCUTS.find((s) => s.id === shortcutId);
  if (!def) return undefined;
  return formatShortcut(def.keys);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface CommandPaletteProviderProps {
  children: ReactNode;
}

export function CommandPaletteProvider({ children }: CommandPaletteProviderProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [dynamicCommands, setDynamicCommands] = useState<Command[]>([]);

  // --- open / close helpers ------------------------------------------------

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // --- Cmd+K hotkey --------------------------------------------------------

  const cmdKShortcut = SHORTCUTS.find((s) => s.id === 'command-palette');
  useHotkey({
    key: cmdKShortcut?.keys.key ?? 'k',
    meta: cmdKShortcut?.keys.meta,
    ctrl: cmdKShortcut?.keys.ctrl,
    shift: cmdKShortcut?.keys.shift,
    alt: cmdKShortcut?.keys.alt,
    action: toggle,
    ignoreInputs: false, // must work even when an input is focused
  });

  // --- Dynamic command registration ----------------------------------------

  const registerCommand = useCallback((cmd: Command) => {
    setDynamicCommands((prev) => {
      if (prev.some((c) => c.id === cmd.id)) return prev;
      return [...prev, cmd];
    });
  }, []);

  const unregisterCommand = useCallback((id: string) => {
    setDynamicCommands((prev) => prev.filter((c) => c.id !== id));
  }, []);

  // --- Built-in navigation commands ----------------------------------------

  const builtInCommands: Command[] = useMemo(
    () => [
      {
        id: 'nav-dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        section: 'Navigation',
        action: () => {
          navigate(ROUTES.DASHBOARD);
          close();
        },
      },
      {
        id: 'nav-workflows',
        label: 'Workflows',
        icon: GitBranch,
        section: 'Navigation',
        action: () => {
          navigate(ROUTES.WORKFLOWS);
          close();
        },
      },
      {
        id: 'nav-executions',
        label: 'Executions',
        icon: Play,
        section: 'Navigation',
        action: () => {
          navigate(ROUTES.EXECUTIONS);
          close();
        },
      },
      {
        id: 'nav-agents',
        label: 'Agents',
        icon: Bot,
        section: 'Navigation',
        action: () => {
          navigate(ROUTES.AGENTS);
          close();
        },
      },
      {
        id: 'nav-audit',
        label: 'Audit Log',
        icon: Shield,
        section: 'Navigation',
        action: () => {
          navigate(ROUTES.AUDIT);
          close();
        },
      },
      {
        id: 'nav-admin',
        label: 'Admin',
        icon: Settings,
        section: 'Navigation',
        action: () => {
          navigate(ROUTES.ADMIN);
          close();
        },
      },
      {
        id: 'action-new-workflow',
        label: 'New Workflow',
        icon: Plus,
        section: 'Actions',
        shortcut: shortcutDisplayFor('new-workflow'),
        action: () => {
          navigate(ROUTES.WORKFLOW_NEW);
          close();
        },
      },
      {
        id: 'action-new-agent',
        label: 'New Agent',
        icon: UserPlus,
        section: 'Actions',
        shortcut: shortcutDisplayFor('new-agent'),
        action: () => {
          navigate(ROUTES.AGENT_NEW);
          close();
        },
      },
    ],
    [navigate, close],
  );

  // --- Merged command list -------------------------------------------------

  const commands = useMemo(
    () => [...builtInCommands, ...dynamicCommands],
    [builtInCommands, dynamicCommands],
  );

  // --- Context value -------------------------------------------------------

  const value = useMemo<CommandPaletteContextValue>(
    () => ({ isOpen, open, close, commands, registerCommand, unregisterCommand }),
    [isOpen, open, close, commands, registerCommand, unregisterCommand],
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandPalette />
    </CommandPaletteContext.Provider>
  );
}
